import React from "react";
import { useTranslation } from "react-i18next";

// Standorte der Voelker mit Koordinaten fuer die Wetterabfrage.
const LOCATIONS = [
  { name: "Dachlissen ZH", variety: "Frühlingsblüte · Wald", color: "#8B857A", lat: 47.246, lon: 8.462 },
  { name: "Malcantone TI", variety: "Primavera · Estate", color: "#3a7a5a", lat: 46.017, lon: 8.867 },
  { name: "Falera GR", variety: "Alpenwiese", color: "#475f6d", lat: 46.799, lon: 9.233 },
];

// WMO-Wettercodes, wie Open-Meteo sie liefert — abgebildet auf
// Uebersetzungsschluessel unter tracht.Weather.*
const WEATHER_CODES = {
  0: "clear", 1: "mostlyClear", 2: "cloudy", 3: "overcast",
  45: "fog", 48: "rimeFog",
  51: "drizzle", 53: "drizzle", 55: "heavyDrizzle",
  56: "freezingDrizzle", 57: "freezingDrizzle",
  61: "lightRain", 63: "rain", 65: "heavyRain",
  66: "freezingRain", 67: "freezingRain",
  71: "lightSnow", 73: "snow", 75: "heavySnow", 77: "snowGrains",
  80: "showers", 81: "showers", 82: "heavyShowers",
  85: "snowShowers", 86: "snowShowers",
  95: "thunderstorm", 96: "thunderstormHail", 99: "thunderstormHail",
};

const WEATHER_URL =
  "https://api.open-meteo.com/v1/forecast" +
  "?latitude=" + LOCATIONS.map(l => l.lat).join(",") +
  "&longitude=" + LOCATIONS.map(l => l.lon).join(",") +
  "&current=temperature_2m,weather_code,wind_speed_10m" +
  "&timezone=Europe%2FZurich";

const CLOCK_INTERVAL_MS = 30 * 1000;
const WEATHER_INTERVAL_MS = 15 * 60 * 1000;

// Die Zeitzone bleibt immer Europe/Zurich — angezeigt wird die Schweizer
// Zeit am Bienenstand, nicht die des Besuchers. Nur Datums- und
// Zahlenformat folgen der gewaehlten Sprache.
const LOCALE_BY_LANG = { de: "de-CH", en: "en-GB", it: "it-CH" };

function TrachtWidget() {
  const { t, i18n } = useTranslation();
  const locale = LOCALE_BY_LANG[(i18n.language || "de").slice(0, 2)] || "de-CH";

  const dateFormat = React.useMemo(() => new Intl.DateTimeFormat(locale, {
    timeZone: "Europe/Zurich", day: "numeric", month: "long", year: "numeric",
  }), [locale]);
  const timeFormat = React.useMemo(() => new Intl.DateTimeFormat(locale, {
    timeZone: "Europe/Zurich", hour: "2-digit", minute: "2-digit",
  }), [locale]);

  const [now, setNow] = React.useState(() => new Date());
  // null = laedt noch, false = Abruf fehlgeschlagen, Array = Messwerte
  const [weather, setWeather] = React.useState(null);

  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), CLOCK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const load = () => {
      fetch(WEATHER_URL)
        .then(res => {
          if (!res.ok) throw new Error("Wetterdienst antwortet mit " + res.status);
          return res.json();
        })
        .then(data => {
          if (cancelled) return;
          // Bei mehreren Koordinaten liefert Open-Meteo ein Array, bei einer
          // einzelnen ein Objekt — beides auf ein Array normalisieren.
          const entries = Array.isArray(data) ? data : [data];
          setWeather(entries.map(e => (e && e.current ? e.current : null)));
        })
        .catch(() => {
          if (!cancelled) setWeather(false);
        });
    };

    load();
    const id = setInterval(load, WEATHER_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const renderReadout = index => {
    const noData = <span className="loc-weather-pending">{t("tracht.No data")}</span>;
    if (weather === null) return <span className="loc-weather-pending">…</span>;
    if (weather === false) return noData;
    const current = weather[index];
    if (!current) return noData;
    const code = WEATHER_CODES[current.weather_code];
    const label = code ? t("tracht.Weather." + code) : "-";
    return (
      <>
        <span className="loc-weather-temp">{Math.round(current.temperature_2m)} °C</span>
        <span className="loc-weather-desc">{label}</span>
        <span className="loc-weather-wind">{Math.round(current.wind_speed_10m)} km/h</span>
      </>
    );
  };

  return (
    <div className="bienen-widget">
      <div className="widget-header">
        <div className="widget-title">{t("tracht.Title")}</div>
        <div className="widget-date">
          {dateFormat.format(now)} · {timeFormat.format(now)}
        </div>
      </div>
      {LOCATIONS.map((loc, i) => (
        <div className="loc-row" key={loc.name}>
          <div className="loc-swatch" style={{ background: loc.color }}></div>
          <div className="loc-info">
            <div className="loc-name">{loc.name}</div>
            <div className="loc-variety">{loc.variety}</div>
          </div>
          <div className="loc-weather">{renderReadout(i)}</div>
        </div>
      ))}
      <div className="widget-footer">{t("tracht.Footer")}</div>
    </div>
  );
}

export default TrachtWidget;
